<template>
  <section id="autoTopupRegistrationsList">
    <h3 class="offset-bottom-2">{{ $t('autotopupregistrations.overview_headline') }}</h3>

    <transition name="fade" mode="out-in" appear>
      <VueGoodTable v-bind="table">
        <template #table-row-after="{ row, index }">
          <td nowrap>
            <div>
              <VButton
                v-if="showDetermineInitiatorButton(row)"
                :ref="`initiatorButton_${row.originalIndex}`"
                class="btn-default"
                :label="$to('autotopupregistrations.determine_initiator_button_label')"
                :title="$to('autotopupregistrations.determine_initiator_button_tooltip')"
                @click.stop="determineInitiator(row)"
              >
                <span class="sr-only">{{ $to('autotopupregistrations.determine_initiator_button_label') }}</span>
              </VButton>
              <span v-else v-focus tabindex="-1">{{ row.translatedCreatedBy }}</span>
            </div>
          </td>
          <td nowrap>
            <div class="action-buttons">
              <VButton
                class="btn-default btn-icon detail-column"
                :title="$to('autotopupregistrations.details_button_label')"
                @click.stop="selectRow(row, index)"
              >
                <span class="sr-only">{{ $to('autotopupregistrations.details_button_label') }}</span>
                <VSvgIcon name="information_2" variant="outlined" />
              </VButton>
              <VButton
                v-if="showRechargeTransactionButton(row)"
                :title="$to('autotopupregistrations.show_recharging_transactions_button_label')"
                class="btn-default btn-icon transaction-column"
                @click.stop="showRechargingTransactions(row)"
              >
                <span class="sr-only">{{ $to('autotopupregistrations.show_recharging_transactions_button_label') }}</span>
                <VSvgIcon name="context-menu" variant="outlined" />
              </VButton>
              <VButton
                v-if="showDeleteRegistrationButton(row)"
                :title="$to('autotopupregistrations.delete_button_label')"
                class="btn-default btn-icon deletion-column"
                @click.stop="deleteRow(row, index)"
              >
                <span class="sr-only">{{ $to('autotopupregistrations.delete_button_label') }}</span>
                <VSvgIcon name="move-to-trash" variant="outlined" />
              </VButton>
            </div>
          </td>
        </template>
        <template #table-column="{ column }">
          <VTableHeader :columns="table.columns" :column="column" />
        </template>
      </VueGoodTable>
    </transition>
  </section>
</template>

<script>
import selectListColumns from './selectListColumns';
import TableMixin from '@/mixins/table';
import VButton from '@/components/Button';
import TableUtilitiesMixin from '@/mixins/tableUtilities';
import formatCurrency from '@/mixins/formatCurrency';
import { VueGoodTable } from 'vue-good-table';
import VTableHeader from '@/components/Table/Header';
import { dateUrlFormat, formatDateTime } from '@/utils/date';
import { mapGetters } from 'vuex';
import { RECHARGE_TRANSACTIONS_OVERVIEW } from '@/router/route-types';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import Proxy from '@/proxies/AutoTopUpRegistrationsProxy';
import focus from '@/directives/focus';
import VSvgIcon from '@/components/UI/VSvgIcon.vue';
export default {
  name: 'AutoTopupRegistrationsList',

  directives: {
    focus,
  },

  components: {
    VSvgIcon,
    VueGoodTable,
    VTableHeader,
    VButton,
  },

  mixins: [TableMixin, TableUtilitiesMixin, formatCurrency],

  props: {
    rows: {
      type: Array,
      required: true,
    },

    selectedRow: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      table: {
        columns: selectListColumns,
        rows: null,
        defaultSortBy: { field: 'idString', type: 'desc' },
      },
      selectedAutoTopUpRegistration: null,
      autoTopupRegistrationsProxy: new Proxy(),
    };
  },

  computed: {
    ...mapGetters('system', ['hasFunctionDeleteAutoTopUpRegistration', 'hasAccessRechargeTransactions']),
  },

  watch: {
    rows: {
      handler: 'onRowsChanged',
      immediate: true,
    },
    selectedRow: {
      handler: 'onSelectedRowChanged',
      immediate: true,
    },
  },

  methods: {
    showDetermineInitiatorButton(row) {
      return row.createdBy === 'UNIDENTIFIED' && row.requestorId;
    },

    async determineInitiator(row) {
      this.$refs[`initiatorButton_${row.originalIndex}`].disabled = true;
      await this.autoTopupRegistrationsProxy
        .determineInitiator(row.requestorId)
        .then((result) => (this.rows[row.originalIndex].createdBy = result.determinedInitiator))
        .finally(() => this.onRowsChanged());
    },

    selectRow(row) {
      this.selectedAutoTopUpRegistration = this.table.rows[row.originalIndex];
      this.$emit('selected', this.selectedAutoTopUpRegistration);
    },

    showDeleteRegistrationButton(row) {
      return row.status === 'CREATED' && this.hasFunctionDeleteAutoTopUpRegistration;
    },

    deleteRow(row) {
      this.selectedAutoTopUpRegistration = this.table.rows[row.originalIndex];
      this.$emit('delete', this.selectedAutoTopUpRegistration);
    },

    showRechargingTransactions(row) {
      const maxTransactionDate = new Date(
        Math.max.apply(
          null,
          row.latestTopUp.map((item) => parseISO(item.topUpDatetime))
        )
      );
      const periodFrom = format(new Date(0), dateUrlFormat);
      const periodUntil = format(maxTransactionDate, dateUrlFormat);
      this.$router.push({
        name: RECHARGE_TRANSACTIONS_OVERVIEW,
        params: { autoTopupId: row.id, from: periodFrom, to: periodUntil },
      });
    },

    showRechargeTransactionButton(row) {
      return row.latestTopUp?.length > 0 && this.hasAccessRechargeTransactions;
    },

    onRowsChanged() {
      this.table.rows = this.translateRows(this.rows);

      this.addFilterDropDownListByField(this.table, 'translatedStatus');
      this.addFilterDropDownListByField(this.table, 'createdBy');
      this.handleDefaultSearchFields(this.table);
    },

    onSelectedRowChanged() {
      this.selectedAutoTopUpRegistration = this.selectedRow;
    },

    translateRows(rows) {
      return rows.map((oldRow) => {
        const row = Object.assign({}, oldRow);
        row.idString = row.id.toString();
        row.translatedStatus = this.$t(`autotopupregistrations.status_${row.status}`);
        row.creationDatetime = formatDateTime(row.creationDatetime);
        row.deletionDatetime = row.deletionDatetime ? formatDateTime(row.deletionDatetime) : null;
        row.formattedAmount = this.formatCurrency(row.amount, row.amountUnits);
        row.translatedCreatedBy = this.$tf(`autotopupregistrations.created_by_${row.createdBy}`, null);
        return row;
      });
    },

    tableRowStyle(row) {
      if (this.selectedAutoTopUpRegistration !== null && row.id === this.selectedAutoTopUpRegistration.id) {
        const index = this.rows.findIndex((r) => r.id === row.id);
        this.setAriaSelected(index);
        return 'brand';
      }
      this.setAriaSelected(-1);
      return '';
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.action-buttons {
  display: -ms-grid;
  display: grid;
  -ms-grid-columns: 30% $building-unit 30% $building-unit 30%;
  grid-template: [row] 100% / [details] minmax(50px, 30%) [transactions] minmax(50px, 30%) [delete] minmax(50px, 30%);
  gap: 0 $building-unit;
  justify-items: stretch;
}

.detail-column {
  -ms-grid-row: 1;
  grid-row: 1;
  -ms-grid-column: 1;
  grid-column: details;
}

.transaction-column {
  -ms-grid-row: 1;
  grid-row: 1;
  -ms-grid-column: 3;
  grid-column: transactions;
}

.deletion-column {
  -ms-grid-row: 1;
  grid-row: 1;
  -ms-grid-column: 5;
  grid-column: delete;
}
</style>
